import PropTypes from "prop-types";
import Figure from "../../../Common/Box/Figure";
import useAreaQuery from "../../../../hooks/useAreaQuery";
import { getSingular, getTooltip } from "../../../../utilities";

const number = 6;

const getSeriesColor = (name, theme) => {
  switch (name) {
    case "Heat-stable carbetocin":
      return theme.palette.areas.maternalHealth.methods.heatStableCarbetocin;

    case "Metronidazole":
      return theme.palette.areas.maternalHealth.methods.metronidazole;

    case "Oxytocin":
      return theme.palette.areas.maternalHealth.methods.oxytocin;

    case "Misoprostol":
      return theme.palette.areas.maternalHealth.methods.misoprostol;

    case "Magnesium sulfate":
      return theme.palette.areas.maternalHealth.methods.magnesiumSulfate;

    case "Methyldopa":
      return theme.palette.areas.maternalHealth.methods.methyldopa;

    case "Hydralazine":
      return theme.palette.areas.maternalHealth.methods.hydralazine;

    case "Iron-folic acid":
      return theme.palette.areas.maternalHealth.methods.ironFolicAcid;

    case "Out-of-scope":
      return theme.palette.areas.maternalHealth.methods.outOfScope;

    case "Needed: Not received":
      return theme.palette.areas.maternalHealth.methods.needsDidNotGet;

    default:
      return "black";
  }
};

const options = (data, props) => (theme) => {
  const absolute = props.comparisonMode !== "relative";

  const series = data.map((method) => ({
    name: method.name,
    data: method.countryGroups.map(({ cost }) => cost),
    color: getSeriesColor(method.name, theme),
  }));

  return {
    chart: {
      type: "bar",
      width: 800,
      height: 500,
      backgroundColor: null,
      marginBottom: 70,
    },
    xAxis: {
      categories: data[0].countryGroups.map(({ name }) => name),
      title: undefined,
      labels: {
        autoRotation: undefined,
        useHTML: true,
        style: {
          color: theme.figure.axis.primary,
          fontSize: theme.figure.axis.fontSize,
        },
      },
    },
    yAxis: {
      title: undefined,
      opposite: true,
      reversedStacks: false,
      max: absolute ? undefined : 100,
      labels: {
        formatter: function () {
          return absolute
            ? "$" + this.axis.defaultLabelFormatter.call(this)
            : `${this.value}%`;
        },
        style: {
          color: theme.figure.axis.secondary,
          fontSize: theme.figure.axis.fontSize,
        },
      },
    },
    plotOptions: {
      bar: {
        stacking: absolute ? "normal" : "percent",
        borderWidth: 0,
        groupPadding: 0.1,
      },
    },
    legend: {
      align: "center",
      verticalAlign: "bottom",
      layout: "horizontal",
      reversed: false,
      itemStyle: {
        fontSize: theme.figure.legend.fontSize,
      },
    },
    tooltip: {
      backgroundColor: "white",
      useHTML: true,
      outside: true,
      formatter: function () {
        return getTooltip("Costs", this.y, this.percentage, {
          prefix: "$",
        });
      },
      style: {
        fontSize: "0.875rem",
        color: theme.palette.tooltip.primary,
      },
    },
    exporting: {
      chartOptions: {
        title: undefined,
        subtitle: undefined,
      },
      enabled: false,
    },
    series,
  };
};

const MhCcF6 = (props) => {
  const { areaData: data } = useAreaQuery(
    `/custom/mh/cc/figures/6/${props.regionalUnitId}`
  );

  return (
    <Figure
      id="MH_CC_f6"
      number={number.toString()}
      title="Total Cost for Seven Priority Maternal Health Drugs"
      subtitle={`By ${getSingular(props.regionalUnitName)}, 2019`}
      chartOptions={
        data?.length
          ? options(data, {
              comparisonMode: "absolute",
            })
          : undefined
      }
    />
  );
};

MhCcF6.propTypes = {
  regionalUnitId: PropTypes.number.isRequired,
  regionalUnitName: PropTypes.string.isRequired,
};

export default MhCcF6;
